import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FormatSizeRoundedIcon from "@mui/icons-material/FormatSizeRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import EditableImage from "../landingEditor/EditableImage";
import { getLandingSegments, landingSegmentAnchor } from "../landingEditor/landingLayout";
import { normalizeCalendlyUrl } from "../../../utils/calendly";
import {
  DEFAULT_SEGMENT_VISUAL_STYLE,
  getSegmentVisualStyle,
  updateSegmentVisualStyle,
} from "./segmentVisualStyle";
import {
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
  SEGMENT_FIELD_TYPES,
} from "./segmentRegistry";

const TAB_INDEX = { content: 0, colors: 1, style: 1, type: 1, image: 0, behavior: 2 };
const INDEX_TAB = ["content", "design", "behavior"];
const CONTENT_SECTION_BY_TOOL = Object.freeze({
  image: "image-content",
});
const DESIGN_SECTION_BY_TOOL = Object.freeze({
  colors: "colors",
  style: "advanced",
  type: "type",
});

const FONT_PRESETS = [
  {
    id: "friendly",
    label: "Cercana",
    heading: "Exo 2",
    body: "Asap",
    url: "https://fonts.googleapis.com/css2?family=Asap:wght@400;500;600;700&family=Exo+2:wght@600;700;800;900&display=swap",
  },
  {
    id: "modern",
    label: "Moderna",
    heading: "Poppins",
    body: "Inter",
    url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@600;700;800;900&display=swap",
  },
  {
    id: "strong",
    label: "Comercial",
    heading: "Montserrat",
    body: "Open Sans",
    url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Open+Sans:wght@400;500;600;700&display=swap",
  },
  {
    id: "elegant",
    label: "Elegante",
    heading: "Playfair Display",
    body: "Lato",
    url: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=Playfair+Display:wght@600;700;800;900&display=swap",
  },
];

const COLOR_DEFAULTS = {
  primaryColor: "#F35A3A",
  secondaryColor: "#19242F",
  surfaceColor: "#FFFFFF",
  backgroundColor: "#EEF0F5",
  textColor: "#1A1A2E",
  mutedTextColor: "#5B5D73",
};

const PALETTE_LABELS = [
  "Principal",
  "Secundario",
  "Superficie",
  "Fondo general",
  "Texto principal",
];

const PALETTE_THEME_PATHS = [
  "theme.primaryColor",
  "theme.secondaryColor",
  "theme.surfaceColor",
  "theme.backgroundColor",
  "theme.textColor",
];

const THEME_COLOR_TOKEN_PATHS = Object.freeze({
  "theme.primaryColor": "designTokens.colors.primary",
  "theme.secondaryColor": "designTokens.colors.secondary",
  "theme.surfaceColor": "designTokens.colors.surface",
  "theme.backgroundColor": "designTokens.colors.background",
  "theme.textColor": "designTokens.colors.text",
  "theme.mutedTextColor": "designTokens.colors.mutedText",
  "theme.dividerColor": "designTokens.colors.divider",
});

function paletteFromForm(form) {
  return [
    form?.theme?.primaryColor || COLOR_DEFAULTS.primaryColor,
    form?.theme?.secondaryColor || COLOR_DEFAULTS.secondaryColor,
    form?.theme?.surfaceColor || COLOR_DEFAULTS.surfaceColor,
    form?.theme?.backgroundColor || COLOR_DEFAULTS.backgroundColor,
    form?.theme?.textColor || COLOR_DEFAULTS.textColor,
  ].map(toColorInput);
}

function siteColorChanges(path, value) {
  const changes = { [path]: value };
  const tokenPath = THEME_COLOR_TOKEN_PATHS[path];
  if (tokenPath) changes[tokenPath] = value;
  return changes;
}

function paletteChanges(palette) {
  return Object.assign(
    {},
    ...PALETTE_THEME_PATHS.map((path, index) =>
      siteColorChanges(path, toColorInput(palette[index]))
    )
  );
}

function hexRgb(color) {
  const value = toColorInput(color).slice(1);
  return [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}

function relativeLuminance(color) {
  const channels = hexRgb(color).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(first, second) {
  const light = Math.max(relativeLuminance(first), relativeLuminance(second));
  const dark = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (light + 0.05) / (dark + 0.05);
}

function palettePermutations(values) {
  if (values.length <= 1) return [values];
  return values.flatMap((value, index) =>
    palettePermutations(values.filter((_, current) => current !== index)).map((rest) => [value, ...rest])
  );
}

function randomizedReadablePalette(palette) {
  const values = palette.map(toColorInput);
  const readable = palettePermutations(values).filter((candidate) =>
    contrastRatio(candidate[3], candidate[4]) >= 3 &&
    contrastRatio(candidate[2], candidate[4]) >= 3
  );
  const options = readable.length ? readable : palettePermutations(values);
  if (options.length <= 1) return values;

  const currentKey = values.join("|");
  const alternatives = options.filter((candidate) => candidate.join("|") !== currentKey);
  const candidates = alternatives.length ? alternatives : options;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

const INSPECTOR_COLLECTION_IMAGE_LIMIT = 6;
const EMPTY_SEGMENT_PROPERTIES = Object.freeze([]);

function getAtPath(source, path, fallback = "") {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, rawKey) => {
      const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
      return current?.[key];
    }, source);
  return value === undefined || value === null ? fallback : value;
}

const CUSTOM_SECTION_LAYOUT_OPTIONS = Object.freeze([
  { value: "image-right", label: "Título y contenido a la izquierda" },
  { value: "image-left", label: "Título y contenido a la derecha" },
  { value: "centered", label: "Título y contenido centrados" },
]);

const CUSTOM_SECTION_IMAGE_POSITION_OPTIONS = Object.freeze([
  { value: "center", label: "Centro" },
  { value: "top", label: "Arriba" },
  { value: "bottom", label: "Abajo" },
  { value: "left", label: "Izquierda" },
  { value: "right", label: "Derecha" },
]);

function editableLayoutSegment(form, sectionId) {
  const segments = Array.isArray(form?.layout?.segments) ? form.layout.segments : [];
  const index = segments.findIndex((segment) => segment?.id === sectionId);
  return index >= 0 ? { segment: segments[index], index } : { segment: null, index: -1 };
}

function customSectionDefinition(segment, layoutIndex) {
  if (!segment || segment.type !== "custom" || layoutIndex < 0) return null;
  const root = `layout.segments.${layoutIndex}`;
  const customRoot = `${root}.data.custom`;
  const title = String(segment?.data?.custom?.title || "").trim();
  const visualDefault = { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...(segment.visualStyle || {}) };

  return {
    id: segment.id,
    type: "custom",
    label: title || "Nueva sección",
    visibilityPath: `${root}.visible`,
    defaultVisible: segment.visible !== false,
    requiresProducts: false,
    properties: [
      { path: `${root}.visible`, type: SEGMENT_FIELD_TYPES.BOOLEAN, label: "Mostrar esta sección", defaultValue: true },
      { path: `${customRoot}.eyebrow`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Texto superior (opcional)", maxLength: 180, defaultValue: "" },
      { path: `${customRoot}.title`, type: SEGMENT_FIELD_TYPES.TEXTAREA, label: "Título", maxLength: 220, defaultValue: "Nueva sección" },
      { path: `${customRoot}.description`, type: SEGMENT_FIELD_TYPES.TEXTAREA, label: "Contenido", maxLength: 3200, defaultValue: "" },
      { path: `${customRoot}.youtubeUrl`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Video de YouTube (opcional)", maxLength: 2000, defaultValue: "" },
      {
        path: `${customRoot}.calendlyUrl`,
        type: SEGMENT_FIELD_TYPES.URL,
        label: "Calendly (opcional)",
        maxLength: 2000,
        defaultValue: "",
        validator: "calendly",
        helperText: "Pegá el enlace HTTPS de un evento de Calendly. Se renderiza el calendario dentro de esta sección.",
      },
      { path: `${customRoot}.ctaLabel`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Texto del botón (opcional)", maxLength: 100, defaultValue: "" },
      { path: `${customRoot}.ctaUrl`, type: SEGMENT_FIELD_TYPES.URL, label: "Destino del botón", maxLength: 2000, defaultValue: "", destinationPicker: true },
      { path: `${customRoot}.imageUrl`, type: SEGMENT_FIELD_TYPES.IMAGE, label: "Imagen de la sección (opcional)", maxLength: 2000, defaultValue: "" },
      { path: `${customRoot}.imageAlt`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Texto alternativo de la imagen", maxLength: 220, defaultValue: "" },
      { path: `${customRoot}.imagePosition`, type: SEGMENT_FIELD_TYPES.SELECT, label: "Encuadre de la imagen", options: CUSTOM_SECTION_IMAGE_POSITION_OPTIONS, defaultValue: "center" },
      { path: `${customRoot}.layout`, type: SEGMENT_FIELD_TYPES.SELECT, label: "Posición del título y contenido", options: CUSTOM_SECTION_LAYOUT_OPTIONS, defaultValue: "image-right" },
      { path: "theme.primaryColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Color principal", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#F35A3A" },
      { path: "theme.secondaryColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Color secundario", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#19242F" },
      { path: "theme.surfaceColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Superficie", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#FFFFFF" },
      { path: "theme.backgroundColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Fondo general", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#EEF0F5" },
      { path: "theme.textColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Texto principal", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#1A1A2E" },
      {
        path: `${root}.visualStyle`,
        type: SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE,
        key: "visualStyle",
        label: "Estilo visual del segmento",
        sectionId: "custom",
        defaultValue: visualDefault,
        limits: {
          titleSize: { min: 1, max: 3, step: 1 },
          imageSize: { min: 1, max: 3, step: 1 },
          spacing: { min: 1, max: 3, step: 1 },
          textStyle: { min: 1, max: 3, step: 1 },
        },
      },
    ],
  };
}

function withSectionBackgroundProperties(definition, segment, layoutIndex) {
  if (!definition || !segment || layoutIndex < 0) return definition;
  // Spotlight ya tiene un fondo nativo real (catalog.spotlight.backgroundImageUrl).
  // No duplicamos el mismo control con otro almacenamiento.
  if (segment.type === "spotlight") return definition;
  const root = `layout.segments.${layoutIndex}.visualStyle`;
  const backgroundProperties = [
    {
      path: `${root}.backgroundImageUrl`,
      type: SEGMENT_FIELD_TYPES.IMAGE,
      label: "Imagen de fondo de la sección",
      maxLength: 2000,
      defaultValue: "",
      positionPath: `${root}.backgroundImagePosition`,
    },
    {
      path: `${root}.backgroundImagePosition`,
      type: SEGMENT_FIELD_TYPES.SELECT,
      label: "Posición de la imagen de fondo",
      options: CUSTOM_SECTION_IMAGE_POSITION_OPTIONS,
      defaultValue: "center",
    },
  ];

  const existing = new Set((definition.properties || []).map((property) => property.path));
  return {
    ...definition,
    properties: [
      ...(definition.properties || []),
      ...backgroundProperties.filter((property) => !existing.has(property.path)),
    ],
  };
}

const CONTENT_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.TEXT,
  SEGMENT_FIELD_TYPES.TEXTAREA,
  SEGMENT_FIELD_TYPES.URL,
  SEGMENT_FIELD_TYPES.EMAIL,
  SEGMENT_FIELD_TYPES.IMAGE,
]);

const BEHAVIOR_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.URL,
  SEGMENT_FIELD_TYPES.BOOLEAN,
]);

const DIRECT_CONTENT_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.TEXT,
  SEGMENT_FIELD_TYPES.TEXTAREA,
  SEGMENT_FIELD_TYPES.EMAIL,
]);

const COLLECTION_CONTENT_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.TEXT,
  SEGMENT_FIELD_TYPES.TEXTAREA,
  SEGMENT_FIELD_TYPES.EMAIL,
  SEGMENT_FIELD_TYPES.URL,
]);

const HIGHLIGHT_ICON_COMPONENTS = Object.freeze({
  star: StarRoundedIcon,
  support: SupportAgentRoundedIcon,
  verified: VerifiedRoundedIcon,
  speed: BoltRoundedIcon,
  quality: WorkspacePremiumRoundedIcon,
  local: StorefrontRoundedIcon,
  security: ShieldRoundedIcon,
  autoawesome: AutoAwesomeRoundedIcon,
  event: EventAvailableRoundedIcon,
  groups: GroupsRoundedIcon,
  photo: PhotoCameraRoundedIcon,
  design: DesignServicesRoundedIcon,
});

const COLLECTION_ITEM_LABELS = Object.freeze({
  alt: "Texto alternativo",
  description: "Descripción",
  imageAlt: "Texto alternativo",
  name: "Nombre",
  query: "Búsqueda asociada",
  subtitle: "Descripción",
  title: "Título",
});

function collectionTextFields(property) {
  if (!property?.item) return [];
  if (COLLECTION_CONTENT_FIELD_TYPES.has(property.item.type)) {
    return [{ key: "", label: property.label || "Texto", type: property.item.type, maxLength: property.item.maxLength }];
  }
  if (!Array.isArray(property.item.fields)) return [];
  return property.item.fields
    .filter((field) =>
      COLLECTION_CONTENT_FIELD_TYPES.has(field.type) ||
      (field.type === SEGMENT_FIELD_TYPES.SELECT && field.key === "icon")
    )
    .map((field) => ({
      ...field,
      label: field.label || COLLECTION_ITEM_LABELS[field.key] || "Texto",
    }));
}

function registeredFieldValue(form, field, fallback = "") {
  const registeredFallback = field?.fallbackPath
    ? getAtPath(form, field.fallbackPath, fallback)
    : field?.type === SEGMENT_FIELD_TYPES.COLLECTION
      ? []
      : CONTENT_FIELD_TYPES.has(field?.type)
        ? fallback
        : field?.defaultValue ?? fallback;
  return getAtPath(form, field?.path, registeredFallback);
}

function inheritedColorValue(form, field) {
  if (field?.fallbackPath) {
    return getAtPath(form, field.fallbackPath, field.fallbackValue || field.defaultValue || "");
  }
  if (field?.fallbackValue !== undefined) return field.fallbackValue;
  return field?.defaultValue || "";
}

function sectionColorSeed(form, field) {
  if (field?.overrideSeedPath) {
    return getAtPath(form, field.overrideSeedPath, inheritedColorValue(form, field));
  }
  if (field?.overrideSeedValue !== undefined) return field.overrideSeedValue;
  return inheritedColorValue(form, field);
}

function isSupportedColor(value) {
  const candidate = String(value || "").trim();
  if (!candidate) return true;
  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", candidate);
  }
  return /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|var\(--[^)]+\)|[a-z]+)$/i.test(candidate);
}

function registryFieldError(field, value) {
  if (!field) return "";
  const text = String(value ?? "");
  if (Number.isFinite(field.maxLength) && text.length > field.maxLength) {
    return `Máximo ${field.maxLength} caracteres.`;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR && !isSupportedColor(text)) {
    return "Ingresá un color CSS válido, por ejemplo #F35A3A.";
  }
  if (
    field.type === SEGMENT_FIELD_TYPES.SELECT &&
    Array.isArray(field.options) &&
    field.options.length &&
    !field.options.some((option) => option.value === value)
  ) {
    return "Elegí una opción válida.";
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER && text !== "") {
    const number = Number(value);
    if (!Number.isFinite(number)) return "Ingresá un número válido.";
    if (Number.isFinite(field.min) && number < field.min) return `El mínimo es ${field.min}.`;
    if (Number.isFinite(field.max) && number > field.max) return `El máximo es ${field.max}.`;
  }
  if (field.validator === "calendly" && text.trim() && !normalizeCalendlyUrl(text)) {
    return "Usá un enlace HTTPS de un evento válido de Calendly (calendly.com/usuario/evento).";
  }
  return "";
}


function isSectionAnchorTarget(value) {
  const target = String(value || "").trim();
  return target.startsWith("#") && !target.startsWith("#/");
}

function manualDestinationLinkError(field, value) {
  const target = String(value || "").trim();
  const baseError = registryFieldError(field, target);
  if (baseError) return baseError;
  if (!target) return "";
  if (isSectionAnchorTarget(target)) {
    return "Para ir a una sección de la landing, elegila desde el selector. No escribas el ancla a mano.";
  }
  if (!/^(https?:\/\/|\/|#\/|mailto:|tel:)/i.test(target)) {
    return "Ingresá un link válido: https://…, /ruta, mailto:… o tel:….";
  }
  return "";
}

function segmentAvailableAsDestination(form, segment, productsEnabled) {
  if (!segment || ["navbar", "footer"].includes(segment.type)) return false;
  if (segment.type === "custom") return segment.visible !== false;
  const definition = getSegmentDefinition(segment.type);
  if (!definition) return false;
  if (definition.requiresProducts && (!productsEnabled || getAtPath(form, "sections.showProducts", true) === false)) return false;
  if (segment.type === "spotlight" && getAtPath(form, "catalog.spotlight.enabled", false) !== true) return false;
  if (segment.visible === false) return false;
  if (!definition.visibilityPath) return true;
  return getAtPath(form, definition.visibilityPath, definition.defaultVisible) !== false;
}

function ButtonDestinationEditor({ field, value, options, disabled, onCommit }) {
  const current = String(value || "").trim();
  const currentIsSection = isSectionAnchorTarget(current);
  const selectedSection = currentIsSection && options.some((option) => option.value === current)
    ? current
    : "";
  const [linkDraft, setLinkDraft] = React.useState(currentIsSection ? "" : current);
  const [linkError, setLinkError] = React.useState("");

  React.useEffect(() => {
    setLinkDraft(currentIsSection ? "" : current);
    setLinkError("");
  }, [current, currentIsSection]);

  const commitLink = () => {
    const nextValue = String(linkDraft || "").trim();
    const error = manualDestinationLinkError(field, nextValue);
    setLinkError(error);
    if (error || nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  const chooseSection = (nextValue) => {
    setLinkError("");
    setLinkDraft("");
    if (nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  return (
    <Stack spacing={0.9}>
      <TextField
        fullWidth
        size="small"
        label="Link"
        value={linkDraft}
        disabled={disabled}
        error={Boolean(linkError)}
        inputProps={{ maxLength: field.maxLength }}
        placeholder="https://sitio.com, /ruta, mailto:…"
        helperText={linkError || "Para un sitio, una ruta, email o teléfono. Las secciones de esta landing se eligen abajo."}
        onChange={(event) => {
          setLinkDraft(event.target.value);
          if (linkError) setLinkError("");
        }}
        onBlur={commitLink}
      />
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>O ir a una sección</InputLabel>
        <Select
          label="O ir a una sección"
          value={selectedSection}
          onChange={(event) => chooseSection(event.target.value)}
        >
          <MenuItem value=""><em>Sin sección</em></MenuItem>
          {options.map((option) => (
            <MenuItem key={`${field.path}:${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          Sólo aparecen secciones visibles que existen en esta landing. El ancla se asigna automáticamente.
        </FormHelperText>
      </FormControl>
    </Stack>
  );
}

function RegisteredBehaviorTextField({ field, value, disabled, onCommit }) {
  const current = String(value ?? "");
  const [draft, setDraft] = React.useState(current);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setDraft(current);
    setError("");
  }, [current, field.path]);

  const commit = () => {
    const nextValue = String(draft || "").trim();
    const nextError = registryFieldError(field, nextValue);
    setError(nextError);
    if (nextError || nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={field.label}
      value={draft}
      disabled={disabled}
      error={Boolean(error)}
      inputProps={{ maxLength: field.maxLength }}
      placeholder={field.validator === "calendly" ? "https://calendly.com/tu-negocio/turno" : undefined}
      helperText={error || field.helperText || "Se guarda al salir del campo."}
      onChange={(event) => {
        setDraft(event.target.value);
        if (error) setError("");
      }}
      onBlur={commit}
    />
  );
}

function navbarPropertyAvailable(form, property, productsEnabled) {
  const path = String(property?.path || "");
  if (!path.startsWith("navigation.")) return true;

  const layoutTypes = new Set(getLandingSegments(form).map((segment) => segment.type));
  const aboutEnabled = layoutTypes.has("about") && getAtPath(form, "sections.showAbout", true) !== false;
  const highlightsEnabled = layoutTypes.has("highlights") && getAtPath(form, "sections.showHighlights", true) !== false;
  const productsSectionEnabled =
    productsEnabled &&
    getAtPath(form, "sections.showProducts", true) !== false &&
    ["spotlight", "featured", "brands", "categories"].some((type) => layoutTypes.has(type));
  const contactEnabled = layoutTypes.has("contact") && getAtPath(form, "sections.showContact", true) !== false;

  if (/^navigation\.(aboutLabel|aboutUrl|showAboutLink)$/.test(path)) return aboutEnabled;
  if (/^navigation\.(highlightsLabel|highlightsUrl|showHighlightsLink)$/.test(path)) return highlightsEnabled;
  if (/^navigation\.(productsLabel|productsUrl|allProductsLabel|showProductsLink|megaMenu|showMegaMenu|loadingCategoriesText|emptyCategoriesText|categoriesErrorText)/.test(path)) {
    return productsSectionEnabled;
  }
  if (/^navigation\.(contactLabel|contactUrl|showContactLink)$/.test(path)) return contactEnabled;
  return true;
}

function NavbarButtonsEditor({ property, items, options, disabled, onCommit }) {
  const safeItems = Array.isArray(items) ? items : [];
  const maxItems = Number.isFinite(property?.limits?.maxItems) ? property.limits.maxItems : 8;
  const visibleItems = safeItems.slice(0, maxItems);
  const [errors, setErrors] = React.useState({});

  const updateButton = (index, patch) => {
    const nextItems = [...safeItems];
    nextItems[index] = { ...(nextItems[index] || {}), ...patch };
    onCommit(property.path, nextItems);
  };

  const addButton = () => {
    if (safeItems.length >= maxItems) return;
    onCommit(property.path, [
      ...safeItems,
      {
        id: `nav-${Date.now().toString(36)}`,
        label: "",
        behavior: "section",
        target: "",
      },
    ]);
  };

  const removeButton = (index) => {
    setErrors((current) => {
      const next = { ...current };
      delete next[index];
      return next;
    });
    onCommit(property.path, safeItems.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        bgcolor: "#F7F8FA",
        border: "1px solid rgba(25,36,47,.08)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography sx={{ color: "#19242F", fontWeight: 900 }}>
            Botones de la navbar
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
            Agregá botones libres. Primero definí el texto y después su comportamiento.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || safeItems.length >= maxItems}
          onClick={addButton}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, flexShrink: 0 }}
        >
          Agregar botón
        </Button>
      </Stack>

      {!visibleItems.length ? (
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
          Todavía no agregaste botones personalizados a la navbar.
        </Typography>
      ) : null}

      <Stack spacing={1.15}>
        {visibleItems.map((item, index) => {
          const label = String(item?.label || "");
          const behavior = item?.behavior === "link" ? "link" : "section";
          const target = String(item?.target || "").trim();
          const selectedSection = behavior === "section" && options.some((option) => option.value === target)
            ? target
            : "";
          const linkError = errors[index] || "";

          return (
            <Box key={item?.id || `${property.path}:${index}`} sx={{ p: 1.15, borderRadius: 2.25, bgcolor: "#fff" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.9 }}>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", fontWeight: 850 }}>
                  Botón {index + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  disabled={disabled}
                  onClick={() => removeButton(index)}
                  sx={{ minWidth: 0, px: 1, textTransform: "none", fontWeight: 800 }}
                >
                  Quitar
                </Button>
              </Stack>

              <Stack spacing={1}>
                <TextField
                  key={`${item?.id || index}:label:${label}`}
                  fullWidth
                  size="small"
                  label="Texto del botón"
                  defaultValue={label}
                  disabled={disabled}
                  inputProps={{ maxLength: 80 }}
                  helperText="Este es el texto que se verá en la navbar."
                  onBlur={(event) => {
                    const nextValue = event.target.value.trim();
                    if (nextValue !== label) updateButton(index, { label: nextValue });
                  }}
                />

                <FormControl fullWidth size="small" disabled={disabled}>
                  <InputLabel>Comportamiento</InputLabel>
                  <Select
                    label="Comportamiento"
                    value={behavior}
                    onChange={(event) => {
                      setErrors((current) => ({ ...current, [index]: "" }));
                      updateButton(index, { behavior: event.target.value, target: "" });
                    }}
                  >
                    <MenuItem value="section">Deslizar a una sección</MenuItem>
                    <MenuItem value="link">Abrir un link</MenuItem>
                  </Select>
                </FormControl>

                {behavior === "section" ? (
                  <FormControl fullWidth size="small" disabled={disabled || !options.length}>
                    <InputLabel>Sección de destino</InputLabel>
                    <Select
                      label="Sección de destino"
                      value={selectedSection}
                      onChange={(event) => updateButton(index, { target: event.target.value })}
                    >
                      <MenuItem value=""><em>Elegí una sección</em></MenuItem>
                      {options.map((option) => (
                        <MenuItem key={`${item?.id || index}:${option.value}`} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {options.length
                        ? "Sólo aparecen secciones existentes y habilitadas. No se pueden escribir anclas manualmente."
                        : "No hay secciones habilitadas disponibles como destino."}
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <TextField
                    key={`${item?.id || index}:target:${target}`}
                    fullWidth
                    size="small"
                    label="Link"
                    defaultValue={target}
                    disabled={disabled}
                    error={Boolean(linkError)}
                    inputProps={{ maxLength: 2000 }}
                    placeholder="https://sitio.com o /ruta"
                    helperText={linkError || "Podés usar un sitio, una ruta, email o teléfono. Para secciones usá el comportamiento Deslizar."}
                    onBlur={(event) => {
                      const nextValue = event.target.value.trim();
                      const error = manualDestinationLinkError(
                        { type: SEGMENT_FIELD_TYPES.URL, maxLength: 2000 },
                        nextValue
                      );
                      setErrors((current) => ({ ...current, [index]: error }));
                      if (!error && nextValue !== target) updateButton(index, { target: nextValue });
                    }}
                  />
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function levelOptionsFromRegistry(visualField, key, labels) {
  const limits = visualField?.limits?.[key] || { min: 1, max: 3, step: 1 };
  const min = Number.isFinite(limits.min) ? limits.min : 1;
  const max = Number.isFinite(limits.max) ? limits.max : 3;
  const step = Number.isFinite(limits.step) && limits.step > 0 ? limits.step : 1;
  const options = [];
  for (let value = min; value <= max; value += step) {
    options.push({ value, label: labels[value] || String(value) });
  }
  return options;
}

function normalizeVisualPatch(visualField, patch) {
  return Object.fromEntries(Object.entries(patch).map(([key, rawValue]) => {
    const limits = visualField?.limits?.[key];
    if (!limits) return [key, rawValue];
    const min = Number.isFinite(limits.min) ? limits.min : rawValue;
    const max = Number.isFinite(limits.max) ? limits.max : rawValue;
    const value = Math.max(min, Math.min(max, Number(rawValue)));
    return [key, value];
  }));
}

function toColorInput(value) {
  const candidate = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(candidate)) return candidate;
  if (/^#[0-9a-fA-F]{3}$/.test(candidate)) {
    return `#${candidate.slice(1).split("").map((char) => char + char).join("")}`;
  }
  const rgb = candidate.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return `#${rgb.slice(1, 4).map((part) => Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, "0")).join("")}`;
  return "#000000";
}

function directImagePositionField(properties, imageField) {
  const candidates = [];
  if (imageField.positionPath) {
    candidates.push(imageField.positionPath);
  }
  if (imageField.path.endsWith("imageUrl")) {
    candidates.push(imageField.path.replace(/imageUrl$/, "imagePosition"));
  }
  if (imageField.path.endsWith("backgroundImageUrl")) {
    candidates.push(imageField.path.replace(/backgroundImageUrl$/, "backgroundImagePosition"));
  }
  return properties.find((property) =>
    property.type === SEGMENT_FIELD_TYPES.SELECT && candidates.includes(property.path)
  );
}

function imageAssetKey(path) {
  return String(path || "image")
    .replace(/\.\d+\./g, "-")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

function imageFieldsFromRegistry(definition, form) {
  const properties = definition?.properties || [];
  const directFields = properties
    .filter((property) => property.editable !== false && property.type === SEGMENT_FIELD_TYPES.IMAGE)
    .map((property) => {
      const positionField = directImagePositionField(properties, property);
      return {
        ...property,
        key: imageAssetKey(property.path),
        positionPath: positionField?.path,
        positionLabel: positionField?.label,
        positionOptions: positionField?.options || [],
        positionDefaultValue: positionField?.defaultValue,
      };
    });

  const collectionFields = properties.flatMap((property) => {
    if (property.editable === false || property.type !== SEGMENT_FIELD_TYPES.COLLECTION) return [];
    const items = getAtPath(form, property.path, []);
    if (!Array.isArray(items) || !items.length) return [];
    const itemFields = Array.isArray(property.item?.fields) ? property.item.fields : [];
    const images = itemFields.filter((itemField) => itemField.type === SEGMENT_FIELD_TYPES.IMAGE);
    if (!images.length) return [];
    const positionField = itemFields.find((itemField) =>
      itemField.type === SEGMENT_FIELD_TYPES.SELECT && /position/i.test(itemField.key || "")
    );
    const registeredMax = Number.isFinite(property.limits?.maxItems)
      ? property.limits.maxItems
      : items.length;
    const visibleItems = items.slice(0, Math.min(registeredMax, INSPECTOR_COLLECTION_IMAGE_LIMIT));

    return visibleItems.flatMap((item, index) => images.map((imageField) => {
      const path = `${property.path}.${index}.${imageField.key}`;
      return {
        ...imageField,
        path,
        label: item?.name || item?.title || item?.label || `${property.label || "Imagen"} ${index + 1}`,
        key: imageAssetKey(path),
        positionPath: positionField ? `${property.path}.${index}.${positionField.key}` : undefined,
        positionLabel: positionField?.label || "Posición de la imagen",
        positionOptions: positionField?.options || [],
        collectionPath: property.path,
        collectionIndex: index,
        collectionLength: visibleItems.length,
        collectionLabel: property.label || "Elementos",
      };
    }));
  });

  const collectionRootsWithImages = new Set(
    collectionFields
      .filter((field) => Boolean(getAtPath(form, field.path, "")))
      .map((field) => String(field.collectionPath || "").split(".")[0])
  );
  const filteredDirectFields = directFields.filter((field) => {
    const root = String(field.path || "").split(".")[0];
    return !collectionRootsWithImages.has(root);
  });

  return [...filteredDirectFields, ...collectionFields];
}

function styleFieldsFromRegistry(definition, imageFields) {
  const imagePositionPaths = new Set(imageFields.map((field) => field.positionPath).filter(Boolean));
  return (definition?.properties || []).filter((property) =>
    property.editable !== false &&
    property.type === SEGMENT_FIELD_TYPES.SELECT &&
    !imagePositionPaths.has(property.path) &&
    /(\.style|\.layout)$/.test(property.path)
  );
}

function LevelSelector({ label, value, options, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        onChange={(_, next) => next && onChange(next)}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", fontWeight: 850 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

function InspectorAccordion({
  id,
  expanded,
  onChange,
  icon: Icon,
  title,
  description,
  children,
}) {
  return (
    <Accordion
      expanded={expanded === id}
      onChange={(_, nextExpanded) => onChange(nextExpanded ? id : false)}
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid rgba(25,36,47,.09)",
        borderRadius: "14px !important",
        bgcolor: "#fff",
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        aria-controls={`segment-inspector-${id}-content`}
        id={`segment-inspector-${id}-header`}
        sx={{
          px: 1.4,
          minHeight: 58,
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.10)",
              color: "#F35A3A",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#19242F", fontWeight: 950, lineHeight: 1.15 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)", lineHeight: 1.3 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        id={`segment-inspector-${id}-content`}
        sx={{ px: 1.4, pt: 0.5, pb: 1.5 }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function LandingSegmentInspector({
  open,
  sectionId,
  initialTool = "content",
  initialImagePath = "",
  form,
  productsEnabled,
  saving,
  onClose,
  onCommit,
  onCommitMany,
  onUploadImage,
  onRemoveImage,
}) {
  const mobile = useMediaQuery("(max-width:760px)");
  const compactEditor = useMediaQuery("(max-width:1100px)");
  const [tab, setTab] = React.useState(TAB_INDEX[initialTool] ?? 0);
  const [expandedContentSection, setExpandedContentSection] = React.useState(
    CONTENT_SECTION_BY_TOOL[initialTool] || false
  );
  const [expandedDesignSection, setExpandedDesignSection] = React.useState(
    DESIGN_SECTION_BY_TOOL[initialTool] || false
  );
  const [colorDraft, setColorDraft] = React.useState({});
  const [paletteDraft, setPaletteDraft] = React.useState(() => paletteFromForm(form));
  const activeLayout = React.useMemo(
    () => editableLayoutSegment(form, sectionId),
    [form, sectionId]
  );
  const customInstance = activeLayout.segment?.type === "custom" ? activeLayout.segment : null;
  const definition = React.useMemo(() => {
    const baseDefinition = customInstance
      ? customSectionDefinition(customInstance, activeLayout.index)
      : getSegmentDefinition(sectionId);
    return withSectionBackgroundProperties(baseDefinition, activeLayout.segment, activeLayout.index);
  }, [activeLayout.index, activeLayout.segment, customInstance, sectionId]);
  const registeredProperties = React.useMemo(() => {
    const properties = definition?.properties || EMPTY_SEGMENT_PROPERTIES;
    if (sectionId !== "navbar") return properties;
    return properties.filter((property) => navbarPropertyAvailable(form, property, productsEnabled));
  }, [definition, form, productsEnabled, sectionId]);
  const label = definition?.label || "Sección";
  const visualField = registeredProperties.find(
    (property) => property.type === SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE
  );
  const visual = customInstance
    ? { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...(customInstance.visualStyle || {}) }
    : getSegmentVisualStyle(form?.theme, sectionId);
  const colorFields = React.useMemo(
    () => registeredProperties.filter(
      (property) => property.editable !== false && property.type === SEGMENT_FIELD_TYPES.COLOR
    ),
    [registeredProperties]
  );
  const siteColorFields = React.useMemo(
    () => colorFields.filter((field) =>
      field.colorScope === "site" || String(field.path || "").startsWith("theme.")
    ),
    [colorFields]
  );
  const sectionColorFields = React.useMemo(
    () => colorFields.filter((field) => !siteColorFields.includes(field)),
    [colorFields, siteColorFields]
  );
  const hasFullSitePalette = React.useMemo(
    () => PALETTE_THEME_PATHS.every((path) => siteColorFields.some((field) => field.path === path)),
    [siteColorFields]
  );
  const extraSiteColorFields = React.useMemo(
    () => siteColorFields.filter((field) => !hasFullSitePalette || !PALETTE_THEME_PATHS.includes(field.path)),
    [hasFullSitePalette, siteColorFields]
  );
  const imageFields = React.useMemo(
    () => imageFieldsFromRegistry(definition, form),
    [definition, form]
  );
  const imageCardRefs = React.useRef({});
  const styleFields = React.useMemo(
    () => styleFieldsFromRegistry(definition, imageFields),
    [definition, imageFields]
  );
  const visibilityPath = definition?.visibilityPath;
  const behaviorFields = React.useMemo(
    () => registeredProperties.filter((property) =>
      property.editable !== false &&
      property.path !== visibilityPath &&
      BEHAVIOR_FIELD_TYPES.has(property.type)
    ),
    [registeredProperties, visibilityPath]
  );
  const internalAnchorOptions = React.useMemo(() =>
    getLandingSegments(form)
      .filter((segment) => segmentAvailableAsDestination(form, segment, productsEnabled))
      .map((segment) => ({
        value: landingSegmentAnchor(segment),
        label: segment.type === "custom"
          ? (segment.data?.custom?.title || segment.anchor || segment.id || "Sección libre")
          : (getSegmentDefinition(segment.type)?.label || segment.type),
      }))
      .filter((option) => option.value),
    [form, productsEnabled]
  );
  const contentFields = React.useMemo(
    () => registeredProperties.filter((property) =>
      property.editable !== false &&
      property.path !== "hero.overlay" &&
      DIRECT_CONTENT_FIELD_TYPES.has(property.type)
    ),
    [registeredProperties]
  );
  const contentCollections = React.useMemo(
    () => registeredProperties
      .filter((property) =>
        property.editable !== false &&
        property.type === SEGMENT_FIELD_TYPES.COLLECTION &&
        collectionTextFields(property).length > 0
      ),
    [registeredProperties]
  );
  const visibilityField = registeredProperties.find(
    (property) => property.path === visibilityPath && property.type === SEGMENT_FIELD_TYPES.BOOLEAN
  );
  const overlayField = registeredProperties.find((property) => property.path === "hero.overlay");
  const moduleUnavailable = Boolean(definition?.requiresProducts && !productsEnabled);
  const visible = visibilityPath
    ? getAtPath(form, visibilityPath, definition?.defaultVisible ?? true) !== false
    : true;
  const paletteValidationField = React.useMemo(() => ({
    type: SEGMENT_FIELD_TYPES.COLOR,
    maxLength: Math.max(18, ...siteColorFields.map((field) => field.maxLength || 0)),
  }), [siteColorFields]);
  const colorErrors = React.useMemo(
    () => Object.fromEntries(
      colorFields.map((field) => [field.path, registryFieldError(field, colorDraft[field.path])])
    ),
    [colorDraft, colorFields]
  );
  const paletteErrors = React.useMemo(
    () => hasFullSitePalette
      ? paletteDraft.map((value) => registryFieldError(paletteValidationField, value))
      : [],
    [hasFullSitePalette, paletteDraft, paletteValidationField]
  );
  const hasColorErrors = Object.values(colorErrors).some(Boolean) || paletteErrors.some(Boolean);
  const spacingOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "spacing", {
      1: "Compacto",
      2: "Equilibrado",
      3: "Amplio",
    }),
    [visualField]
  );
  const titleSizeOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "titleSize", {
      1: "Pequeño",
      2: "Normal",
      3: "Grande",
    }),
    [visualField]
  );
  const textStyleOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "textStyle", {
      1: "Suave",
      2: "Firme",
      3: "Impactante",
    }),
    [visualField]
  );
  const imageSizeOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "imageSize", {
      1: "Pequeña",
      2: "Normal",
      3: "Grande",
    }),
    [visualField]
  );

  React.useEffect(() => {
    if (!open) return;
    setTab(TAB_INDEX[initialTool] ?? 0);
    setExpandedContentSection(CONTENT_SECTION_BY_TOOL[initialTool] || false);
    setExpandedDesignSection(DESIGN_SECTION_BY_TOOL[initialTool] || false);
  }, [initialTool, open, sectionId]);

  React.useEffect(() => {
    if (!open || initialTool !== "image" || !initialImagePath) return undefined;
    const timer = window.setTimeout(() => {
      imageCardRefs.current[initialImagePath]?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [initialImagePath, initialTool, open, sectionId, imageFields.length]);

  React.useEffect(() => {
    const next = {};
    colorFields.forEach((field) => {
      const isSiteColor = field.colorScope === "site" || String(field.path || "").startsWith("theme.");
      if (!isSiteColor) {
        next[field.path] = getAtPath(form, field.path, "") || "";
        return;
      }
      next[field.path] = registeredFieldValue(form, field, field.defaultValue || "#000000");
    });
    setColorDraft(next);
    setPaletteDraft(paletteFromForm(form));
  }, [colorFields, form, sectionId]);

  const updateVisual = (patch) => {
    if (!visualField) return;
    const normalizedPatch = normalizeVisualPatch(visualField, patch);
    if (customInstance && activeLayout.index >= 0) {
      onCommit(
        `layout.segments.${activeLayout.index}.visualStyle`,
        { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...(customInstance.visualStyle || {}), ...normalizedPatch }
      );
      return;
    }
    const template = updateSegmentVisualStyle(form?.theme?.template, sectionId, normalizedPatch);
    onCommit("theme.template", template);
  };

  const setPaletteColor = (index, value) => {
    const path = PALETTE_THEME_PATHS[index];
    setPaletteDraft((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
    if (path) {
      setColorDraft((current) => ({ ...current, [path]: value }));
    }
  };

  const setSectionColorOverride = (field, enabled) => {
    setColorDraft((current) => ({
      ...current,
      [field.path]: enabled
        ? current[field.path] || sectionColorSeed(form, field) || "#000000"
        : "",
    }));
  };

  const applyColors = () => {
    if (hasColorErrors) return;
    const changes = {};
    siteColorFields.forEach((field) => {
      Object.assign(changes, siteColorChanges(field.path, colorDraft[field.path] || field.defaultValue || ""));
    });
    sectionColorFields.forEach((field) => {
      changes[field.path] = colorDraft[field.path] || "";
    });
    if (Object.keys(changes).length) onCommitMany(changes);
  };

  const randomizeColors = () => {
    if (!hasFullSitePalette || paletteErrors.some(Boolean)) return;
    const randomized = randomizedReadablePalette(paletteDraft);
    const changes = paletteChanges(randomized);
    setPaletteDraft(randomized);
    setColorDraft((current) => ({
      ...current,
      ...Object.fromEntries(PALETTE_THEME_PATHS.map((path, index) => [path, randomized[index]])),
    }));
    onCommitMany(changes);
  };

  const resetSectionColors = () => {
    const changes = Object.fromEntries(sectionColorFields.map((field) => [field.path, ""]));
    if (!Object.keys(changes).length) return;
    setColorDraft((current) => ({ ...current, ...changes }));
    onCommitMany(changes);
  };

  const setVisible = (next) => {
    if (!visibilityPath || moduleUnavailable) return;
    if (customInstance && activeLayout.index >= 0) {
      onCommit(visibilityPath, next === true);
      return;
    }
    const changes = buildSegmentVisibilityChanges(sectionId, next);
    const entries = Object.entries(changes);
    if (!entries.length) return;
    if (entries.length === 1) onCommit(entries[0][0], entries[0][1]);
    else onCommitMany(changes);
  };

  const moveCollectionImage = (field, direction) => {
    if (!field?.collectionPath || !Number.isInteger(field.collectionIndex)) return;
    const items = getAtPath(form, field.collectionPath, []);
    if (!Array.isArray(items) || items.length < 2) return;

    const from = field.collectionIndex;
    const to = from + direction;
    const visibleLength = Number(field.collectionLength || items.length);
    if (from < 0 || from >= items.length || to < 0 || to >= items.length) return;
    if (to >= visibleLength) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onCommit(field.collectionPath, next);
  };

  const drawerPaper = compactEditor
    ? {
        width: "100%",
        maxWidth: "100%",
        maxHeight: mobile ? "88vh" : "74vh",
        borderRadius: "20px 20px 0 0",
      }
    : { width: 410, maxWidth: "92vw" };

  return (
    <Drawer
      anchor={compactEditor ? "bottom" : "right"}
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          ...drawerPaper,
          ...(compactEditor ? {
            "& .MuiButton-root": { minHeight: 44 },
            "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
            "& .MuiInputBase-root": { minHeight: 44 },
            "& .MuiFormControlLabel-root": { minHeight: 44 },
            "& input[type='color']": { minWidth: 48, minHeight: 44 },
          } : {}),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: compactEditor ? (mobile ? "88vh" : "74vh") : "100%",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 2, pt: 1.7, pb: 1.1 }}>
          <Box>
            <Typography variant="overline" sx={{ color: "#F35A3A", fontWeight: 950, letterSpacing: 1 }}>
              EDITAR SEGMENTO
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F", lineHeight: 1.1 }}>
              {label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={saving} aria-label="Cerrar opciones">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="fullWidth"
          sx={{
            px: 1,
            minHeight: 46,
            borderBottom: "1px solid rgba(25,36,47,.09)",
            "& .MuiTab-root": {
              minHeight: 46,
              minWidth: 0,
              px: 1,
              textTransform: "none",
              fontWeight: 900,
            },
          }}
        >
          <Tab icon={<TextFieldsRoundedIcon />} iconPosition="start" label="Contenido" />
          <Tab icon={<PaletteRoundedIcon />} iconPosition="start" label="Diseño" />
          <Tab icon={<VisibilityRoundedIcon />} iconPosition="start" label="Comportamiento" />
        </Tabs>

        <Box sx={{ p: 2, overflowY: "auto", flex: 1, bgcolor: "#F7F8FA" }}>
          {INDEX_TAB[tab] === "content" ? (
            <Stack spacing={1.5}>
              {!definition ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento todavía no tiene una definición registrada.
                </Alert>
              ) : null}
              {moduleUnavailable ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento requiere el módulo de productos o servicios para poder editar su contenido visible.
                </Alert>
              ) : null}

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(25,36,47,.09)",
                }}
              >
                <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                  Textos y contenido
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                  Editá títulos, descripciones, etiquetas de botones y colecciones sin mezclar enlaces ni opciones de visibilidad.
                </Typography>
                {contentFields.length || contentCollections.length ? (
                  <Stack spacing={1.25} sx={{ mt: 1.4 }}>
                    {contentFields.map((field) => {
                      const value = registeredFieldValue(form, field, "");
                      return (
                        <TextField
                          key={`${field.path}:${String(value)}`}
                          fullWidth
                          size="small"
                          type={field.type === SEGMENT_FIELD_TYPES.EMAIL ? "email" : "text"}
                          label={field.label}
                          defaultValue={value ?? ""}
                          multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA}
                          minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined}
                          disabled={saving || moduleUnavailable}
                          inputProps={{ maxLength: field.maxLength }}
                          helperText="Opcional. Si lo dejás vacío, ese texto no se muestra."
                          onBlur={(event) => {
                            const nextValue = event.target.value;
                            if (nextValue !== value && !registryFieldError(field, nextValue)) {
                              onCommit(field.path, nextValue);
                            }
                          }}
                        />
                      );
                    })}

                    {contentCollections.map((property) => {
                      const items = registeredFieldValue(form, property, []);
                      const safeItems = Array.isArray(items) ? items : [];
                      const fields = collectionTextFields(property);
                      const maxItems = Number.isFinite(property.limits?.maxItems)
                        ? property.limits.maxItems
                        : safeItems.length;
                      const visibleItems = safeItems.slice(0, maxItems);

                      if (property.navbarButtons === true) {
                        return (
                          <NavbarButtonsEditor
                            key={property.path}
                            property={property}
                            items={safeItems}
                            options={internalAnchorOptions}
                            disabled={saving || moduleUnavailable}
                            onCommit={onCommit}
                          />
                        );
                      }

                      const addCollectionItem = () => {
                        if (safeItems.length >= maxItems) return;
                        const primitive = property.item?.type && COLLECTION_CONTENT_FIELD_TYPES.has(property.item.type);
                        if (primitive) {
                          onCommit(property.path, [...safeItems, ""]);
                          return;
                        }
                        const nextItem = {};
                        fields.forEach((itemField) => {
                          if (!itemField.key) return;
                          if (itemField.key === "id") nextItem[itemField.key] = `item-${Date.now().toString(36)}`;
                          else if (itemField.key === "imagePosition") nextItem[itemField.key] = "center";
                          else nextItem[itemField.key] = "";
                        });
                        onCommit(property.path, [...safeItems, nextItem]);
                      };
                      const removeCollectionItem = (index) => {
                        onCommit(property.path, safeItems.filter((_, itemIndex) => itemIndex !== index));
                      };

                      return (
                        <Box
                          key={property.path}
                          sx={{
                            p: 1.25,
                            borderRadius: 2.5,
                            bgcolor: "#F7F8FA",
                            border: "1px solid rgba(25,36,47,.08)",
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography sx={{ color: "#19242F", fontWeight: 900 }}>
                              {property.label}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={saving || moduleUnavailable || safeItems.length >= maxItems}
                              onClick={addCollectionItem}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                            >
                              Agregar
                            </Button>
                          </Stack>
                          {!visibleItems.length ? (
                            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
                              No hay elementos. Podés agregar el primero cuando quieras.
                            </Typography>
                          ) : null}
                          <Stack spacing={1.15}>
                            {visibleItems.map((item, index) => (
                              <Box key={`${property.path}:${index}`} sx={{ p: 1.1, borderRadius: 2.25, bgcolor: "#fff" }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", fontWeight: 850 }}>
                                    {property.label} {index + 1}
                                  </Typography>
                                  <Button
                                    size="small"
                                    color="error"
                                    disabled={saving || moduleUnavailable}
                                    onClick={() => removeCollectionItem(index)}
                                    sx={{ minWidth: 0, px: 1, textTransform: "none", fontWeight: 800 }}
                                  >
                                    Quitar
                                  </Button>
                                </Stack>
                                <Stack spacing={1}>
                                  {fields.map((itemField) => {
                                    const primitive = itemField.key === "";
                                    const value = primitive ? item ?? "" : item?.[itemField.key] ?? "";
                                    if (itemField.type === SEGMENT_FIELD_TYPES.SELECT) {
                                      return (
                                        <FormControl
                                          key={`${property.path}:${index}:${itemField.key || "value"}:${String(value)}`}
                                          fullWidth
                                          size="small"
                                        >
                                          <InputLabel>{itemField.label || "Ícono MUI"}</InputLabel>
                                          <Select
                                            label={itemField.label || "Ícono MUI"}
                                            value={value}
                                            disabled={saving || moduleUnavailable}
                                            onChange={(event) => {
                                              const nextItems = [...safeItems];
                                              nextItems[index] = {
                                                ...(nextItems[index] || {}),
                                                [itemField.key]: event.target.value,
                                              };
                                              onCommit(property.path, nextItems);
                                            }}
                                          >
                                            {(itemField.options || []).map((option) => {
                                              const Icon = HIGHLIGHT_ICON_COMPONENTS[option.value];
                                              return (
                                                <MenuItem key={option.value || "none"} value={option.value}>
                                                  <Stack direction="row" spacing={1} alignItems="center">
                                                    {Icon ? <Icon sx={{ fontSize: 19 }} /> : <Box sx={{ width: 19, height: 19 }} />}
                                                    <span>{option.label}</span>
                                                  </Stack>
                                                </MenuItem>
                                              );
                                            })}
                                          </Select>
                                          <FormHelperText>Opcional. Elegí “Sin ícono” para no mostrar ninguno.</FormHelperText>
                                        </FormControl>
                                      );
                                    }

                                    return (
                                      <TextField
                                        key={`${property.path}:${index}:${itemField.key || "value"}:${String(value)}`}
                                        fullWidth
                                        size="small"
                                        label={primitive ? property.label : itemField.label}
                                        defaultValue={value}
                                        multiline={itemField.type === SEGMENT_FIELD_TYPES.TEXTAREA}
                                        minRows={itemField.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 2 : undefined}
                                        disabled={saving || moduleUnavailable}
                                        inputProps={{ maxLength: itemField.maxLength }}
                                        helperText="Opcional"
                                        onBlur={(event) => {
                                          const nextValue = event.target.value;
                                          if (nextValue === value) return;
                                          const nextItems = [...safeItems];
                                          nextItems[index] = primitive
                                            ? nextValue
                                            : { ...(nextItems[index] || {}), [itemField.key]: nextValue };
                                          onCommit(property.path, nextItems);
                                        }}
                                      />
                                    );
                                  })}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ mt: 1.2, color: "rgba(25,36,47,.58)" }}>
                    Esta sección no tiene textos editables. Usá Imágenes, Diseño o Comportamiento según corresponda.
                  </Typography>
                )}
              </Box>

              {imageFields.length ? (
                <InspectorAccordion
                  id="image-content"
                  expanded={expandedContentSection}
                  onChange={setExpandedContentSection}
                  icon={ImageRoundedIcon}
                  title="Imágenes"
                  description="Elegí, reemplazá, eliminá y ordená imágenes cuando la sección lo permita"
                >
                  <Stack spacing={1.4}>
                    {imageFields.map((field) => {
                      const canReorder = Boolean(
                        field.collectionPath && Number(field.collectionLength || 0) > 1
                      );
                      const first = field.collectionIndex === 0;
                      const last = field.collectionIndex >= Number(field.collectionLength || 0) - 1;

                      return (
                        <Box
                          key={field.path}
                          ref={(node) => {
                            if (node) imageCardRefs.current[field.path] = node;
                            else delete imageCardRefs.current[field.path];
                          }}
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            bgcolor: "#fff",
                            border: field.path === initialImagePath
                              ? "2px solid rgba(243,90,58,.72)"
                              : "1px solid rgba(25,36,47,.09)",
                            boxShadow: field.path === initialImagePath
                              ? "0 0 0 3px rgba(243,90,58,.08)"
                              : "none",
                          }}
                        >
                          <EditableImage
                            value={getAtPath(form, field.path, "")}
                            label={field.label}
                            helperText={field.positionPath
                              ? "El encuadre y el tamaño visual se ajustan desde Diseño."
                              : "El tamaño visual se ajusta desde Diseño."}
                            height={150}
                            disabled={saving || moduleUnavailable}
                            onUpload={(file, options) => onUploadImage(field.path, file, { sectionKey: sectionId, assetKey: field.key }, options)}
                            onRemove={() => onRemoveImage(field.path)}
                          />

                          {visualField || field.positionPath ? (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<TuneRoundedIcon />}
                              disabled={saving || moduleUnavailable}
                              onClick={() => {
                                setTab(1);
                                setExpandedDesignSection("image-design");
                              }}
                              sx={{ mt: 0.8, borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                            >
                              {field.positionPath ? "Ajustar tamaño y encuadre" : "Ajustar tamaño visual"}
                            </Button>
                          ) : null}

                          {canReorder ? (
                            <Box sx={{ mt: 1.25, pt: 1.15, borderTop: "1px solid rgba(25,36,47,.08)" }}>
                              <Typography variant="caption" sx={{ display: "block", mb: 0.8, color: "rgba(25,36,47,.6)", fontWeight: 850 }}>
                                Orden en {String(field.collectionLabel || "la sección").toLowerCase()} · posición {field.collectionIndex + 1} de {field.collectionLength}
                              </Typography>
                              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<KeyboardArrowUpRoundedIcon />}
                                  disabled={saving || moduleUnavailable || first}
                                  onClick={() => moveCollectionImage(field, -1)}
                                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                                >
                                  Mover arriba
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<KeyboardArrowDownRoundedIcon />}
                                  disabled={saving || moduleUnavailable || last}
                                  onClick={() => moveCollectionImage(field, 1)}
                                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                                >
                                  Mover abajo
                                </Button>
                              </Stack>
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                </InspectorAccordion>
              ) : null}

              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Colores, tipografías, espaciado y encuadre están en Diseño. Enlaces, visibilidad y otras reglas están en Comportamiento.
              </Alert>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "design" ? (
            <Stack spacing={1.35}>
              <Box
                sx={{
                  p: 1.45,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(25,36,47,.09)",
                }}
              >
                <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                  Ajustes frecuentes
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.3, mb: 1.45, color: "rgba(25,36,47,.62)" }}>
                  Empezá por la distribución y el aire de la sección. El resto queda agrupado debajo.
                </Typography>

                <Stack spacing={1.65}>
                  {visualField ? (
                    <LevelSelector
                      label="Aire y separación del segmento"
                      value={visual.spacing}
                      options={spacingOptions}
                      onChange={(spacing) => updateVisual({ spacing })}
                    />
                  ) : null}

                  {styleFields.map((field) => {
                    const value = registeredFieldValue(form, field, field.defaultValue || "");
                    const error = registryFieldError(field, value);
                    return (
                      <FormControl key={field.path} fullWidth size="small" error={Boolean(error)}>
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                          label={field.label}
                          value={value}
                          disabled={saving}
                          onChange={(event) => {
                            if (!registryFieldError(field, event.target.value)) {
                              onCommit(field.path, event.target.value);
                            }
                          }}
                        >
                          {(field.options || []).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error ? <FormHelperText>{error}</FormHelperText> : null}
                      </FormControl>
                    );
                  })}
                </Stack>
              </Box>

              <InspectorAccordion
                id="colors"
                expanded={expandedDesignSection}
                onChange={setExpandedDesignSection}
                icon={PaletteRoundedIcon}
                title="Colores"
                description="Tema global y excepciones de este bloque, sin mezclarlos"
              >
                <Stack spacing={1.5}>
                  {siteColorFields.length ? (
                    <Box sx={{ p: 1.25, bgcolor: "#F7F8FA", borderRadius: 3 }}>
                      <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                        Colores del sitio
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                        Estos colores pertenecen al tema global. Al aplicarlos, cambian todos los bloques que los heredan.
                      </Typography>

                      {hasFullSitePalette ? (
                        <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 1 }}>
                          {PALETTE_LABELS.map((paletteLabel, index) => (
                            <Stack
                              key={paletteLabel}
                              direction="row"
                              spacing={0.8}
                              alignItems="center"
                              sx={{ p: 0.8, borderRadius: 2.5, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.08)" }}
                            >
                              <Box
                                component="input"
                                type="color"
                                aria-label={paletteLabel}
                                value={toColorInput(paletteDraft[index])}
                                onChange={(event) => setPaletteColor(index, event.target.value)}
                                sx={{ width: 40, height: 38, p: 0.2, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer", flexShrink: 0 }}
                              />
                              <TextField
                                size="small"
                                fullWidth
                                label={paletteLabel}
                                value={paletteDraft[index] || ""}
                                onChange={(event) => setPaletteColor(index, event.target.value)}
                                inputProps={{ maxLength: paletteValidationField.maxLength }}
                                error={Boolean(paletteErrors[index])}
                                helperText={paletteErrors[index] || " "}
                              />
                            </Stack>
                          ))}
                        </Box>
                      ) : null}

                      {extraSiteColorFields.length ? (
                        <Stack spacing={1} sx={{ mt: hasFullSitePalette ? 0.5 : 1.25 }}>
                          {extraSiteColorFields.map((field) => (
                            <Stack key={field.path} direction="row" spacing={1} alignItems="center" sx={{ p: 0.9, bgcolor: "#fff", borderRadius: 2.5, border: "1px solid rgba(25,36,47,.08)" }}>
                              <Box
                                component="input"
                                type="color"
                                aria-label={field.label}
                                value={toColorInput(colorDraft[field.path])}
                                onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                                sx={{ width: 42, height: 38, p: 0.25, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer", flexShrink: 0 }}
                              />
                              <TextField
                                size="small"
                                fullWidth
                                label={field.label}
                                value={colorDraft[field.path] || ""}
                                onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                                inputProps={{ maxLength: field.maxLength }}
                                error={Boolean(colorErrors[field.path])}
                                helperText={colorErrors[field.path] || " "}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      ) : null}

                      {hasFullSitePalette ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<ShuffleRoundedIcon />}
                          onClick={randomizeColors}
                          disabled={saving || paletteErrors.some(Boolean)}
                          sx={{ mt: 0.75, borderRadius: 3, py: 1.05, textTransform: "none", fontWeight: 900 }}
                        >
                          Probar otra combinación global
                        </Button>
                      ) : null}
                    </Box>
                  ) : null}

                  {sectionColorFields.length ? (
                    <Box sx={{ p: 1.25, bgcolor: "#fff", borderRadius: 3, border: "1px solid rgba(25,36,47,.09)" }}>
                      <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                        Excepciones de esta sección
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.35, mb: 1.15, color: "rgba(25,36,47,.62)" }}>
                        Por defecto este bloque hereda el tema. Activá un color propio sólo cuando necesites que esta sección sea distinta.
                      </Typography>

                      <Stack spacing={1}>
                        {sectionColorFields.map((field) => {
                          const ownValue = String(colorDraft[field.path] || "").trim();
                          const inherited = inheritedColorValue(form, field) || "transparent";
                          const hasOverride = Boolean(ownValue);
                          return (
                            <Box key={field.path} sx={{ p: 1.05, borderRadius: 2.5, bgcolor: "#F7F8FA", border: "1px solid rgba(25,36,47,.07)" }}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: ".9rem", fontWeight: 900, color: "#19242F" }}>
                                    {field.label}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                                    {hasOverride ? "Color propio de este bloque" : `Heredado: ${inherited}`}
                                  </Typography>
                                </Box>
                                <FormControlLabel
                                  sx={{ m: 0, flexShrink: 0 }}
                                  control={(
                                    <Switch
                                      size="small"
                                      checked={hasOverride}
                                      onChange={(event) => setSectionColorOverride(field, event.target.checked)}
                                    />
                                  )}
                                  label={hasOverride ? "Propio" : "Heredar"}
                                />
                              </Stack>

                              {hasOverride ? (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.9 }}>
                                  <Box
                                    component="input"
                                    type="color"
                                    aria-label={field.label}
                                    value={toColorInput(ownValue)}
                                    onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                                    sx={{ width: 44, height: 40, p: 0.25, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer", flexShrink: 0 }}
                                  />
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label="Color propio"
                                    value={ownValue}
                                    onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                                    inputProps={{ maxLength: field.maxLength }}
                                    error={Boolean(colorErrors[field.path])}
                                    helperText={colorErrors[field.path] || " "}
                                  />
                                </Stack>
                              ) : (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.85 }}>
                                  <Box
                                    aria-hidden
                                    sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: inherited, border: "1px solid rgba(25,36,47,.16)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.35)" }}
                                  />
                                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>
                                    Si cambia el tema global, esta sección se actualiza automáticamente.
                                  </Typography>
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : null}

                  {hasColorErrors ? (
                    <Alert severity="error" sx={{ borderRadius: 3 }}>
                      Corregí los colores marcados antes de aplicar los cambios.
                    </Alert>
                  ) : null}

                  <Stack direction={mobile ? "column" : "row"} spacing={1}>
                    {sectionColorFields.length ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<RestartAltRoundedIcon />}
                        onClick={resetSectionColors}
                        disabled={saving}
                        sx={{ borderRadius: 3, textTransform: "none", fontWeight: 900 }}
                      >
                        Quitar excepciones
                      </Button>
                    ) : null}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={applyColors}
                      disabled={saving || hasColorErrors || !colorFields.length}
                      sx={{ borderRadius: 3, bgcolor: "#F35A3A", textTransform: "none", fontWeight: 950 }}
                    >
                      {sectionColorFields.length && !siteColorFields.length ? "Aplicar en esta sección" : "Aplicar colores"}
                    </Button>
                  </Stack>
                </Stack>
              </InspectorAccordion>

              <InspectorAccordion
                id="type"
                expanded={expandedDesignSection}
                onChange={setExpandedDesignSection}
                icon={FormatSizeRoundedIcon}
                title="Tipografía"
                description="Tamaño, fuerza visual y familia de letra"
              >
                <Stack spacing={1.7}>
                  {visualField ? (
                    <>
                      <LevelSelector
                        label="Tamaño de los títulos de este segmento"
                        value={visual.titleSize}
                        options={titleSizeOptions}
                        onChange={(titleSize) => updateVisual({ titleSize })}
                      />
                      <LevelSelector
                        label="Fuerza visual de los títulos"
                        value={visual.textStyle}
                        options={textStyleOptions}
                        onChange={(textStyle) => updateVisual({ textStyle })}
                      />
                      <Divider />
                    </>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      Este segmento no declara ajustes tipográficos propios en el registro.
                    </Alert>
                  )}

                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    Familia tipográfica de toda la página
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                    {FONT_PRESETS.map((preset) => {
                      const selected = form?.theme?.headingFontFamily === preset.heading && form?.theme?.bodyFontFamily === preset.body;
                      return (
                        <Button
                          key={preset.id}
                          variant={selected ? "contained" : "outlined"}
                          onClick={() => onCommitMany({
                            "theme.fontFamily": preset.body,
                            "theme.headingFontFamily": preset.heading,
                            "theme.bodyFontFamily": preset.body,
                            "theme.googleFontsUrl": preset.url,
                          })}
                          sx={{ minHeight: 74, borderRadius: 3, textTransform: "none", fontWeight: 900, bgcolor: selected ? "#19242F" : "#fff" }}
                        >
                          <Stack>
                            <Typography sx={{ fontFamily: preset.heading, fontWeight: 900 }}>{preset.label}</Typography>
                            <Typography variant="caption" sx={{ opacity: .72 }}>{preset.heading} + {preset.body}</Typography>
                          </Stack>
                        </Button>
                      );
                    })}
                  </Box>
                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>
                    La familia de letra se mantiene global para conservar coherencia entre secciones.
                  </Typography>
                </Stack>
              </InspectorAccordion>

              {imageFields.length ? (
                <InspectorAccordion
                  id="image-design"
                  expanded={expandedDesignSection}
                  onChange={setExpandedDesignSection}
                  icon={ImageRoundedIcon}
                  title="Diseño de imágenes"
                  description="Tamaño visual y encuadre, sin mezclar la selección del archivo"
                >
                  <Stack spacing={1.6}>
                    {visualField ? (
                      <LevelSelector
                        label="Tamaño visual de las imágenes de este segmento"
                        value={visual.imageSize}
                        options={imageSizeOptions}
                        onChange={(imageSize) => updateVisual({ imageSize })}
                      />
                    ) : null}

                    {imageFields.filter((field) => field.positionPath && field.positionOptions?.length).map((field) => {
                      const value = getAtPath(
                        form,
                        field.positionPath,
                        field.positionDefaultValue || field.positionOptions[0]?.value || "center"
                      );
                      const positionDefinition = {
                        type: SEGMENT_FIELD_TYPES.SELECT,
                        options: field.positionOptions,
                      };
                      const error = registryFieldError(positionDefinition, value);
                      return (
                        <FormControl key={field.positionPath} fullWidth size="small" error={Boolean(error)}>
                          <InputLabel>{field.positionLabel || `Posición de ${field.label}`}</InputLabel>
                          <Select
                            label={field.positionLabel || `Posición de ${field.label}`}
                            value={value}
                            disabled={saving || moduleUnavailable}
                            onChange={(event) => {
                              if (!registryFieldError(positionDefinition, event.target.value)) {
                                onCommit(field.positionPath, event.target.value);
                              }
                            }}
                          >
                            {field.positionOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {error ? <FormHelperText>{error}</FormHelperText> : null}
                        </FormControl>
                      );
                    })}
                  </Stack>
                </InspectorAccordion>
              ) : null}

              <InspectorAccordion
                id="advanced"
                expanded={expandedDesignSection}
                onChange={setExpandedDesignSection}
                icon={TuneRoundedIcon}
                title="Opciones avanzadas"
                description="Ajustes visuales que normalmente no necesitás cambiar"
              >
                <Stack spacing={1.8}>
                  {overlayField ? (
                    <LevelSelector
                      label="Oscuridad sobre la imagen"
                      value={String(getAtPath(form, overlayField.path, "")).includes(".82") ? 3 : String(getAtPath(form, overlayField.path, "")).includes(".48") ? 1 : 2}
                      options={[{ value: 1, label: "Suave" }, { value: 2, label: "Legible" }, { value: 3, label: "Intensa" }]}
                      onChange={(value) => onCommit(
                        overlayField.path,
                        value === 1
                          ? "linear-gradient(to bottom, rgba(0,0,0,.08), rgba(8,8,20,.48))"
                          : value === 3
                            ? "linear-gradient(to bottom, rgba(0,0,0,.28), rgba(8,8,20,.82))"
                            : "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))"
                      )}
                    />
                  ) : null}

                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
                      Redondez general de tarjetas y botones
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={Number(form?.theme?.borderRadius || 10) <= 5 ? 1 : Number(form?.theme?.borderRadius || 10) >= 20 ? 3 : 2}
                      onChange={(_, next) => next && onCommit("theme.borderRadius", next === 1 ? 4 : next === 3 ? 24 : 10)}
                    >
                      <ToggleButton value={1}>Recto</ToggleButton>
                      <ToggleButton value={2}>Suave</ToggleButton>
                      <ToggleButton value={3}>Redondo</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {!overlayField ? (
                    <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>
                      Esta sección no necesita ajustes avanzados adicionales.
                    </Typography>
                  ) : null}
                </Stack>
              </InspectorAccordion>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "behavior" ? (
            <Stack spacing={1.5}>
              {moduleUnavailable ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento depende del módulo de productos o servicios. Activá ese módulo antes de mostrarlo.
                </Alert>
              ) : null}

              {visibilityPath ? (
                <Box
                  sx={{
                    p: 1.45,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    border: "1px solid rgba(25,36,47,.09)",
                  }}
                >
                  <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                    Visibilidad
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, mb: 1.1, color: "rgba(25,36,47,.62)" }}>
                    Decidí si esta sección forma parte de la página sin alterar su contenido ni su diseño.
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={visible}
                        disabled={saving || moduleUnavailable}
                        onChange={(event) => setVisible(event.target.checked)}
                      />
                    )}
                    label={visible ? "Esta sección está visible" : "Esta sección está oculta"}
                    title={visibilityField?.label || "Visibilidad del segmento"}
                    sx={{ m: 0, width: "100%" }}
                  />
                </Box>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Esta sección no tiene un control de visibilidad independiente.
                </Alert>
              )}

              {behaviorFields.length ? (
                <Box
                  sx={{
                    p: 1.45,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    border: "1px solid rgba(25,36,47,.09)",
                  }}
                >
                  <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                    Enlaces y comportamiento
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, mb: 1.25, color: "rgba(25,36,47,.62)" }}>
                    Configurá destinos y reglas de funcionamiento sin mezclar estos valores con los textos visibles.
                  </Typography>
                  <Stack spacing={1.2}>
                    {behaviorFields.map((field) => {
                      const value = registeredFieldValue(form, field, field.defaultValue ?? "");
                      if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
                        return (
                          <FormControlLabel
                            key={field.path}
                            control={(
                              <Switch
                                checked={value === true}
                                disabled={saving || moduleUnavailable}
                                onChange={(event) => onCommit(field.path, event.target.checked)}
                              />
                            )}
                            label={field.label}
                            sx={{ m: 0 }}
                          />
                        );
                      }

                      if (field.type === SEGMENT_FIELD_TYPES.URL && field.destinationPicker === true) {
                        return (
                          <Box key={`${field.path}:${String(value)}`}>
                            <Typography variant="body2" sx={{ mb: 0.7, fontWeight: 900, color: "#19242F" }}>
                              {field.label}
                            </Typography>
                            <ButtonDestinationEditor
                              field={field}
                              value={value}
                              options={internalAnchorOptions}
                              disabled={saving || moduleUnavailable}
                              onCommit={onCommit}
                            />
                          </Box>
                        );
                      }

                      return (
                        <RegisteredBehaviorTextField
                          key={field.path}
                          field={field}
                          value={value}
                          disabled={saving || moduleUnavailable}
                          onCommit={onCommit}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}

              <Typography variant="caption" sx={{ px: 0.5, color: "rgba(25,36,47,.58)" }}>
                Abrir, cerrar o cambiar de pestaña no modifica valores. Los cambios se aplican solamente cuando usás un control.
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
